import { UseFormReturn } from 'react-hook-form';
import {
  BankBeneficiaryFormValues,
  BankBeneficiaryType,
  CryptoBeneficiaryFormValues,
  mapExistingBeneficiaryToTransferBeneficiary,
  TransferFormValues,
} from './types';
import { BankBeneficiaryFields } from './BankBeneficiaryFields';
import { CryptoBeneficiaryFields } from './CryptoBeneficiaryFields';
import { useBeneficiariesQuery } from '../../../api/beneficiaries';
import {
  BankTransferListItem,
  Button,
  CryptoTransferListItem,
  FormContentIsland,
  InternalTransferListItem,
  ListContentIsland,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { ApiBeneficiary } from '../../../openapi';
import { InternalBeneficiaryFields } from './InternalBeneficiaryFields';
import { BeneficiaryList } from './BeneficiaryList';

export function TransferBeneficiaryStep({
  form,
  internalEnabled = false,
  onFinish,
}: {
  form: UseFormReturn<TransferFormValues>;
  internalEnabled?: boolean;
  onFinish: () => void;
}) {
  const { t } = useTranslation();
  const beneficiariesQuery = useBeneficiariesQuery();
  const type = form.watch('beneficiary.type');

  const startBankTransfer = useCallback(() => {
    form.reset({
      beneficiary: {
        type: 'bank',
        valueType: BankBeneficiaryType.business,
      },
    });
  }, [form]);

  const startCryptoTransfer = useCallback(() => {
    form.reset({
      beneficiary: {
        type: 'crypto',
        value: {
          subtype: 'MYSELF',
        },
      },
    });
  }, [form]);

  const startInternalTransfer = useCallback(() => {
    form.reset({
      beneficiary: {
        type: 'internal',
      },
    });
  }, [form]);

  const startWithBeneficiary = useCallback(
    (beneficiary: ApiBeneficiary) => {
      if (beneficiary.type === 'CRYPTO' && !beneficiary.subtype) {
        form.reset({
          beneficiary: mapExistingBeneficiaryToTransferBeneficiary(beneficiary),
        });
      } else {
        form.reset({
          beneficiary: {
            type: 'existing',
            value: beneficiary,
          },
        });
        onFinish();
      }
    },
    [form, onFinish],
  );

  const reset = (
    <Button type="reset" variant="text" color="secondary">
      {t('Back')}
    </Button>
  );

  switch (type) {
    case 'bank': {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bankForm: UseFormReturn<BankBeneficiaryFormValues> = form as any;
      return (
        <FormContentIsland title={t('Bank recipient')} actions={reset}>
          <BankBeneficiaryFields form={bankForm} />
        </FormContentIsland>
      );
    }

    case 'crypto': {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cryptoForm: UseFormReturn<CryptoBeneficiaryFormValues> = form as any;
      return (
        <FormContentIsland title={t('Crypto recipient')} actions={reset}>
          <CryptoBeneficiaryFields form={cryptoForm} />
        </FormContentIsland>
      );
    }

    case 'internal': {
      return (
        <FormContentIsland title={t('{{companyName}} recipient')} actions={reset}>
          <InternalBeneficiaryFields />
        </FormContentIsland>
      );
    }

    default:
      return (
        <>
          <ListContentIsland title={t('New recipient')}>
            {internalEnabled ? <InternalTransferListItem onClick={startInternalTransfer} /> : null}
            <BankTransferListItem onClick={startBankTransfer} />
            <CryptoTransferListItem onClick={startCryptoTransfer} />
          </ListContentIsland>
          <BeneficiaryList
            beneficiaries={beneficiariesQuery.data ?? []}
            onSelectBeneficiary={startWithBeneficiary}
          />
        </>
      );
  }
}
